import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { catchError, of } from 'rxjs';
import { PayoutsService } from '@/app/services/payouts/payouts.service';
import { CreatorPayoutBalances, CreatorPayoutListItem } from '@/app/common/general-types/payouts.dto';
import { CreatorService, CreatorResponseDto } from '@/app/services/creator/creator.service';
import {
  payoutProviderLabel,
  payoutStatusLabel,
  payoutStatusPillClass,
} from '@/app/pages/admin-options/settings/molecules/admin-payout-labels/admin-payout-labels';
import { MiscService } from '@/app/services/misc/misc.service';

type PayoutCopyField = 'payoutId' | 'walletTransactionId' | 'briefApplicationId';

@Component({
  selector: 'ins-creator-payouts',
  imports: [CurrencyPipe, DatePipe],
  templateUrl: './payouts.html',
  host: { class: 'block h-full min-h-0 w-full min-w-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreatorPayouts implements OnInit {
  readonly balances = signal<CreatorPayoutBalances | undefined>(undefined);
  readonly payouts = signal<CreatorPayoutListItem[] | undefined>(undefined);
  readonly creator = signal<CreatorResponseDto | undefined>(undefined);
  readonly loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  readonly expandedPayoutKeys = signal<string[]>([]);
  readonly copiedItemKey = signal<string | null>(null);

  protected readonly payoutStatusLabel = payoutStatusLabel;
  protected readonly payoutStatusPillClass = payoutStatusPillClass;
  protected readonly payoutProviderLabel = payoutProviderLabel;

  private copiedResetTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private creatorUserId = signal<string | undefined>(undefined);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private payoutsService = inject(PayoutsService);
  private creatorService = inject(CreatorService);
  private miscService = inject(MiscService);

  ngOnInit(): void {
    const creatorId = this.route.parent?.snapshot.paramMap.get('creatorId') ?? undefined;
    this.creatorUserId.set(creatorId);
    if (creatorId) {
      this.loadData(creatorId);
    }
  }

  currencyCode(): string {
    return (this.creator()?.currencyCode ?? 'gbp').toUpperCase();
  }

  payoutRowKey(item: CreatorPayoutListItem, index: number): string {
    return item.id ?? `row-${index}-${item.createdDate}`;
  }

  isExpanded(item: CreatorPayoutListItem, index: number): boolean {
    return this.expandedPayoutKeys().includes(this.payoutRowKey(item, index));
  }

  toggleExpanded(item: CreatorPayoutListItem, index: number): void {
    const key = this.payoutRowKey(item, index);
    if (this.expandedPayoutKeys().includes(key)) {
      this.expandedPayoutKeys.set(this.expandedPayoutKeys().filter((id) => id !== key));
    } else {
      this.expandedPayoutKeys.set([...this.expandedPayoutKeys(), key]);
    }
  }

  getCopyKey(rowKey: string, field: PayoutCopyField): string {
    return `${rowKey}:${field}`;
  }

  isCopied(copyKey: string): boolean {
    return this.copiedItemKey() === copyKey;
  }

  copyId(
    event: Event,
    value: string | null | undefined,
    label: string,
    copyKey: string,
  ): void {
    event.stopPropagation();
    if (!value) {
      return;
    }

    this.miscService.copyToClipboard(value, label);
    this.copiedItemKey.set(copyKey);

    if (this.copiedResetTimeoutId) {
      clearTimeout(this.copiedResetTimeoutId);
    }

    this.copiedResetTimeoutId = setTimeout(() => {
      this.copiedItemKey.set(null);
      this.copiedResetTimeoutId = null;
    }, 2000);
  }

  private loadData(creatorId: string): void {
    this.loadingState.set('loading');

    this.creatorService
      .getCreatorById(creatorId)
      .pipe(catchError(() => of(undefined)), takeUntilDestroyed(this.destroyRef))
      .subscribe((creator) => {
        if (creator) {
          this.creator.set(creator);
        }
      });

    this.payoutsService
      .getCreatorBalances(creatorId)
      .pipe(catchError(() => of(undefined)), takeUntilDestroyed(this.destroyRef))
      .subscribe((balances) => this.balances.set(balances));

    this.payoutsService
      .getCreatorPayouts(creatorId, { skip: 0, take: 50, endDate: new Date() })
      .pipe(
        catchError(() => {
          this.loadingState.set('error');
          return of([] as CreatorPayoutListItem[]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((payouts) => {
        this.payouts.set(payouts);
        this.loadingState.set('loaded');
      });
  }
}
